import moment from 'moment-timezone'
import { Temporal } from 'temporal-polyfill'

const UTC = 'UTC'

const YMD = 'YYYYMMDD'

const YMD_DASH = 'YYYY-MM-DD'

const ONE_HOUR_IN_SECONDS = 3600
const ONE_MINUTE_IN_SECONDS = 60

const convertUTCToUserTimezoneMoment = (
  date: string,
  user: { time_zone: string }
) => moment.tz(date, UTC).tz(user.time_zone)

export const formatUTCAsTimeFromNow = (
  date: string,
  user: { time_zone: string }
) => convertUTCToUserTimezoneMoment(date, user).fromNow()

export const localizeTime = (time: string) => moment.parseZone(time).local()

export const startOfCurrentWeek = moment().startOf('week')

export const endOfCurrentWeek = moment().endOf('week')

export const startOfCurrentWeekYMD = startOfCurrentWeek.format(YMD)

export const endOfCurrentWeekYMD = endOfCurrentWeek.format(YMD)

const startOfCurrentYear = moment().startOf('year')

export const startOfCurrentYearYMD = startOfCurrentYear.format(YMD)

export const startOfCurrentYearYMDDash = startOfCurrentYear.format(YMD_DASH)

const endOfCurrentYear = moment().endOf('year')

export const endOfCurrentYearYMDDash = endOfCurrentYear.format(YMD_DASH)

export const currentDateYMD = moment().format(YMD)

export const currentDateYMDDash = moment().format(YMD_DASH)

export const currentDateEasternYMDDash = moment
  .tz('America/New_York')
  .format(YMD_DASH)

export const formatISODate = (date: string) => moment(date).toISOString()

export const formatYMDDashDate = (date: string) => moment(date).format(YMD_DASH)

export const roundISOStringToNearest15Minutes = (ISOString: string) =>
  Math.floor(moment(ISOString).minute() / 15) * 15
export const convertToUTCYMD = (time: string) =>
  moment.tz(time, 'UTC').format(YMD)

export const subtractOneWeek = (time: string) =>
  moment(time).subtract(1, 'weeks')

export const isToday = (date: string) => moment(date).isSame(moment(), 'day')

const padTime = (time: string) => time.toString().padStart(2, '0')

export const formatTimeGivenSeconds = (secondsGiven: number) => {
  const hours = Math.floor(secondsGiven / ONE_HOUR_IN_SECONDS)
  const minutes = Math.floor(
    (secondsGiven % ONE_HOUR_IN_SECONDS) / ONE_MINUTE_IN_SECONDS
  )
  const seconds = secondsGiven % ONE_MINUTE_IN_SECONDS

  const timeParts = [minutes, seconds]

  if (hours) {
    timeParts.unshift(hours)
  }

  return timeParts
    .map((time, index) => (index === 0 ? time : padTime(time.toString())))
    .join(':')
}

export const getDateTimeXDaysFromNow = (days: number) =>
  moment().add(days, 'days').format()

export const supportedTimezones = new Map([
  ['America/New_York', 'Eastern time'],
  ['America/Chicago', 'Central time'],
  ['America/Denver', 'Mountain time'],
  ['America/Phoenix', 'Arizona time'],
  ['America/Los_Angeles', 'Pacific time'],
  ['America/Anchorage', 'Alaska time'],
  ['Pacific/Honolulu', 'Hawaii time'],
])

export const timezoneName = (timezoneIdentifier: string) =>
  supportedTimezones.get(timezoneIdentifier)

export const isValidDate = (date: string) => moment(date).isValid()

export const isSameDayOrAfter = (date: string, compareToDate: string) =>
  date ? moment(compareToDate).isSameOrAfter(moment(date), 'day') : true

export const isSameDayOrBefore = (date: string, compareToDate: string) =>
  date ? moment(compareToDate).isSameOrBefore(moment(date), 'day') : true

export const getDateOneYearAgo = () => {
  const today = Temporal.Now.plainDateISO()
  const oneYearAgo = today.subtract({ years: 1 })
  return oneYearAgo.toString()
}

export function formatToRelativeTimeIntl(
  dateString: string,
  locale: Intl.LocalesArgument = 'en-US',
  timeZoneId = Temporal.Now.timeZoneId()
) {
  const units = [
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
  ] as const

  const now = Temporal.Now.zonedDateTimeISO(timeZoneId)

  const duration = Temporal.Instant.from(
    dateString.includes('Z') ? dateString : `${dateString}Z`
  )
    .toZonedDateTimeISO(timeZoneId)
    .until(now)
    .negated()
    .round({
      largestUnit: units[0],
      smallestUnit: units.at(-1),
      relativeTo: now,
    })

  const [largestUnit, value] = units
    .map(u => [u, duration[u]] as const)
    .find(([, val]) => val) || ['seconds', 0]

  return value === 0
    ? 'now'
    : new Intl.RelativeTimeFormat(locale, {
        numeric: 'always',
        style: 'long',
      }).format(value, largestUnit)
}
